import { LOCATION_INITIALIZED } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Observable, catchError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CapturedMessage } from '../../core/contract/captured-message.contract';
import { LanguageService } from '../../core/services/language.service';
import { RouteCheckerService } from '../../core/services/route-checker.service';
import { UserConfigurationService } from '../../core/services/user-configuration.service';
import { SKIP_AUTH, SKIP_GLOBAL_ERROR_HANDLER } from '../../core/tokens/http-context-tokens';
import { WhitelabelManagerService } from '../../whitelabel/services/whitelabel-manager.service';
import { Languages } from '../enums/languages.enum';

export function TranslateHttpLoaderFactory(http: HttpClient): TranslateLoader {
  return new S3TranslateHttpLoader(http);
}

export class S3TranslateHttpLoader extends TranslateHttpLoader {
  whitelabelManagerService = inject(WhitelabelManagerService);
  private readonly userConfigurationService = inject(UserConfigurationService);
  private readonly routeCheckerService = inject(RouteCheckerService);
  private readonly httpClient: HttpClient;

  constructor(http: HttpClient, prefix?: string, suffix?: string) {
    super(http, prefix, suffix);
    this.httpClient = http;
  }

  public override getTranslation(lang: string): Observable<object> {
    if (lang === Languages.DEV) {
      // Weblate allow only file with this name as base translations file (dev-DEV replaced with en_devel)
      lang = 'en_devel';
    }

    return this.loadTranslationsFromS3(lang).pipe(
      catchError(() => this.loadTranslationsFromS3(this.userConfigurationService.getFallbackLanguage())),
      catchError(() => this.loadLocalTranslations(lang)),
    );
  }

  /**
   * Loads the bundled local translations as a fallback when the remote (S3) translations
   * cannot be loaded. Unlike the S3 requests (raw XMLHttpRequest), this goes through Angular's
   * HttpClient and therefore the RequestInterceptor.
   *
   * On authenticated routes the request must use the normal pipeline (so it carries the auth
   * token like every other request) — we keep the default loader behaviour there.
   *
   * Only on public/unauthenticated routes (e.g. /mail/validate/:code), where Keycloak is never
   * initialized, do we set SKIP_AUTH so the interceptor does not block the request on a token
   * refresh that can never succeed. SKIP_AUTH must never be forced on otherwise.
   */
  private loadLocalTranslations(lang: string): Observable<object> {
    if (!this.routeCheckerService.isPublicRoute()) {
      return super.getTranslation(lang);
    }

    return this.httpClient.get(`${this.prefix}${lang}${this.suffix}`, {
      context: new HttpContext().set(SKIP_AUTH, true).set(SKIP_GLOBAL_ERROR_HANDLER, true),
    });
  }

  private loadTranslationsFromS3(lang: string): Observable<object> {
    return new Observable((subscriber) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${environment.APP_TRANSLATION_BASE_URL}/${lang}.json`);
      xhr.setRequestHeader('x-api-key', environment.APP_TRANSLATION_API_KEY);
      xhr.onload = () => {
        if ((xhr.status >= 200 && xhr.status < 300) || xhr.status === 304) {
          // inject whitelabeled translation overrides into translation object
          const translations = this.whitelabelManagerService.whitelabelTranslations(JSON.parse(xhr.response));
          subscriber.next(translations);
        } else {
          subscriber.error(xhr.statusText);
        }
      };
      xhr.onerror = () => subscriber.error(xhr.statusText);
      xhr.onloadend = () => subscriber.complete();
      xhr.send();
    });
  }
}

export function appInitializerTranslationFactory(
  languageService: LanguageService,
  errorTracker: { captureMessage: (message: CapturedMessage) => void },
  injector: Injector,
) {
  return () => initializeTranslation(languageService, errorTracker, injector);
}

function initializeTranslation(
  languageService: LanguageService,
  errorTracker: { captureMessage: (message: CapturedMessage) => void },
  injector: Injector,
): Promise<null> {
  const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
  return locationInitialized.then(() => {
    return initializeLanguage(languageService, errorTracker);
  });
}

function initializeLanguage(
  languageService: LanguageService,
  errorTracker: { captureMessage: (message: CapturedMessage) => void },
): Promise<null> {
  return new Promise((resolve) => {
    const language = languageService.getCurrentLanguage();
    const handleError = () => {
      errorTracker.captureMessage({
        message: `Problem with '${language}' language initialization.`,
        captureContext: 'warning',
      });
    };

    languageService.use(language).subscribe({
      next: () => {},
      error: handleError,
      complete: () => resolve(null),
    });
  });
}
